import {
	Component, Input, SimpleChanges, ViewChild, ChangeDetectorRef, ChangeDetectionStrategy,
	EventEmitter, Output
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, ValidatorFn } from '@angular/forms';
import { ComponentBase, Changeable } from "common";
import {
	IpAddress, DeviceGroup, DeviceGroupType, DeviceGroupMember,
	DeviceFilter, IncludeScope, Device, DeviceKey
} from 'DiscoveryEngine.TS/Core';
import { List, HashSet } from 'DiscoveryEngine.TS/Utils';
import { ViewerDeviceGroupDto, ViewerDeviceFilterDto } from "UvExplorerServer.Dtos.TS/Viewer";
import { DiscResultsService } from 'discovery';
import { NetViewDto } from 'UvExplorerServer.Dtos.TS/NetViews';
import { DeviceFilterService, NavigationService } from 'netview';
import { MapSettingsComponent } from '../map-settings/map-settings.component';
import { IdentityService } from 'identity';
import { MessageService } from 'base';
import {
	DisplayDeviceListDto, DisplayDeviceListEntryDto, GetDeviceGroupMembersResultDto
} from 'UvExplorerServer.Dtos.TS/Session';


class DeviceGroupMemberListEntry {
	public dataKey: string;
	public name: string;
	public ipAddress: string;
	public description: string;

	public ipAddressTag: IpAddress;
	public get hasIpAddressTag(): boolean {
		return !!this.ipAddressTag;
	}

	public deviceGroupMemberTag: DeviceGroupMember;
	public get hasDeviceGroupMemberTag(): boolean {
		return !!this.deviceGroupMemberTag;
	}

	constructor() {
		this.dataKey = null;
		this.name = "";
		this.ipAddress = "";
		this.description = "";

		this.ipAddressTag = null;
		this.deviceGroupMemberTag = null;
	}
}


@Component({
	selector: 'device-group',
	templateUrl: './device-group.component.html',
	styleUrls: ['./device-group.component.scss'],
	//changeDetection: ChangeDetectionStrategy.OnPush
})
export class DeviceGroupComponent extends ComponentBase implements Changeable {

	@Input()
	public target: ViewerDeviceGroupDto;

	@Input()
	public network: NetViewDto;

	@Input()
	public filterPreviewModal: boolean;

	@Output()
	public onChanges: EventEmitter<boolean>;

	@ViewChild(MapSettingsComponent, { static: true })
	public mapSettings: MapSettingsComponent;

	public form: UntypedFormGroup;

	public groupName: UntypedFormControl;
	public groupType: UntypedFormControl;

	public primBaseIncludeScope: UntypedFormControl;
	public primNameFilters: UntypedFormControl;
	public primIpScopeFilters: UntypedFormControl;
	public primVlanFilters: UntypedFormControl;
	public primOidFilters: UntypedFormControl;
	public primDeviceCategories: string[];
	public primPortFilters: UntypedFormControl;
	public primFilterSummary: string;

	public connBaseIncludeScope: UntypedFormControl;
	public connNameFilters: UntypedFormControl;
	public connIpScopeFilters: UntypedFormControl;
	public connVlanFilters: UntypedFormControl;
	public connOidFilters: UntypedFormControl;
	public connDeviceCategories: string[];
	public connPortFilters: UntypedFormControl;
	public connFilterSummary: string;

	public cols: any[];
	public entries: DeviceGroupMemberListEntry[];
	public selectedDeviceGroupMembers: DeviceGroupMemberListEntry[];

	public previewPrimDevices: DisplayDeviceListDto;
	public previewPrimVisible: boolean;
	public previewConnDevices: DisplayDeviceListDto;
	public previewConnVisible: boolean;

	public copyPrimFilters: List<ViewerDeviceFilterDto>;
	public copyPrimVisible: boolean;
	public copyConnFilters: List<ViewerDeviceFilterDto>;
	public copyConnVisible: boolean;

	public addDevicesDevices: DisplayDeviceListDto;
	public addDevicesVisible: boolean;

	public activeIndex: number;

	public get enableApply(): boolean {
		let validGroupChanges = (!this.form.pristine && this.form.valid);
		let validMapSettingsChanges = this.mapSettings.enableApply;
		return (validGroupChanges || validMapSettingsChanges);
	}

	public doCustomSort(event): void {
		let comparer = function (a: DeviceGroupMemberListEntry, b: DeviceGroupMemberListEntry): number {
			let result = 0;

			switch (event.field) {
				case "ipAddress":
					if (a.ipAddressTag && b.ipAddressTag) {
						result = a.ipAddressTag.CompareTo(b.ipAddressTag);
					}
					else if (a.ipAddressTag) {
						result = 1;
					}
					else if (b.ipAddressTag) {
						result = -1;
					}
					break;
				default:
					// Compare string fields
					result = a[event.field].localeCompare(b[event.field]);
					break;
			}

			return result * event.order;
		}

		this.entries.sort(comparer);
	}

	constructor(private fb: UntypedFormBuilder,
		private discResultsService: DiscResultsService,
		private deviceFilterService: DeviceFilterService,
		private navService: NavigationService,
		private identityService: IdentityService,
		private messageService: MessageService,
		private changeDetector: ChangeDetectorRef) {

		super();

		this.target = null;
		this.network = null;
		this.filterPreviewModal = true;

		this.onChanges = new EventEmitter<boolean>();

		this.primFilterSummary = "";
		this.connFilterSummary = "";

		this.selectedDeviceGroupMembers = [];
		this.cols = [
			{ field: 'name', header: 'Name', sortable: "custom", sortFunction: (event) => this.doCustomSort(event) },
			{ field: 'ipAddress', header: 'IP Address', sortable: "custom", sortFunction: (event) => this.doCustomSort(event) },
			{ field: 'description', header: 'Description', sortable: "custom", sortFunction: (event) => this.doCustomSort(event) },
		];
		this.entries = [];

		this.buildForm();

		this.resetPreviewPrimDevices();
		this.previewPrimVisible = false;
		this.resetPreviewConnDevices();
		this.previewConnVisible = false;

		this.resetCopyPrimFilters();
		this.copyPrimVisible = false;
		this.resetCopyConnFilters();
		this.copyConnVisible = false;

		this.resetAddDevicesDevices();
		this.addDevicesVisible = false;

		this.activeIndex = 0;
	}

	protected buildForm(): void {
		let groupName = "";
		let groupType = "dynamic";

		let primBaseIncludeScope = "AllDevices";
		let primNameFilters = "";
		let primIpScopeFilters = "";
		let primVlanFilters = "";
		let primOidFilters = "";
		let primDeviceCategories = [];
		let primPortFilters = "";

		let connBaseIncludeScope = "NoDevices";
		let connNameFilters = "";
		let connIpScopeFilters = "";
		let connVlanFilters = "";
		let connOidFilters = "";
		let connDeviceCategories = [];
		let connPortFilters = "";

		if (this.target) {
			groupName = this.target.DeviceGroup.Name;
			groupType = "dynamic";
			switch (this.target.DeviceGroup.Type) {
				case DeviceGroupType.Static:
					groupType = "static";
					break;
			}

			primBaseIncludeScope = "AllDevices";
			switch (this.target.DeviceGroup.PrimaryDeviceFilter.BaseIncludeScope) {
				case IncludeScope.AllNetworkDevices:
					primBaseIncludeScope = "AllNetworkDevices";
					break;
				case IncludeScope.AllSnmpDevices:
					primBaseIncludeScope = "AllSnmpDevices";
					break;
			}

			primNameFilters = DeviceGroupComponent.stringListToInputText(this.target.DeviceGroup.PrimaryDeviceFilter.NameFilters);
			primIpScopeFilters = DeviceGroupComponent.stringListToInputText(this.target.DeviceGroup.PrimaryDeviceFilter.IpScopeFilters);
			primVlanFilters = DeviceGroupComponent.stringListToInputText(this.target.DeviceGroup.PrimaryDeviceFilter.VlanFilters);
			primOidFilters = DeviceGroupComponent.stringListToInputText(this.target.DeviceGroup.PrimaryDeviceFilter.OidFilters);
			primPortFilters = DeviceGroupComponent.stringListToCommaInputText(this.target.DeviceGroup.PrimaryDeviceFilter.PortFilters);

			this.target.DeviceGroup.PrimaryDeviceFilter.CategoryFilters.ForEach((catName: string) => {
				primDeviceCategories.push(catName);
			}, this);

			connBaseIncludeScope = "NoDevices";
			switch (this.target.DeviceGroup.ConnectedDeviceFilter.BaseIncludeScope) {
				case IncludeScope.AllDevices:
					connBaseIncludeScope = "AllDevices";
					break;
				case IncludeScope.AllNetworkDevices:
					connBaseIncludeScope = "AllNetworkDevices";
					break;
				case IncludeScope.AllSnmpDevices:
					connBaseIncludeScope = "AllSnmpDevices";
					break;
			}

			connNameFilters = DeviceGroupComponent.stringListToInputText(this.target.DeviceGroup.ConnectedDeviceFilter.NameFilters);
			connIpScopeFilters = DeviceGroupComponent.stringListToInputText(this.target.DeviceGroup.ConnectedDeviceFilter.IpScopeFilters);
			connVlanFilters = DeviceGroupComponent.stringListToInputText(this.target.DeviceGroup.ConnectedDeviceFilter.VlanFilters);
			connOidFilters = DeviceGroupComponent.stringListToInputText(this.target.DeviceGroup.ConnectedDeviceFilter.OidFilters);
			connPortFilters = DeviceGroupComponent.stringListToCommaInputText(this.target.DeviceGroup.ConnectedDeviceFilter.PortFilters);

			this.target.DeviceGroup.ConnectedDeviceFilter.CategoryFilters.ForEach((catName: string) => {
				connDeviceCategories.push(catName);
			}, this);
		}

		this.groupName = new UntypedFormControl(groupName, Validators.required);
		this.groupType = new UntypedFormControl(groupType, Validators.required);

		this.primBaseIncludeScope = new UntypedFormControl(primBaseIncludeScope, Validators.required);
		this.primNameFilters = new UntypedFormControl(primNameFilters, this.validateFilters((filter: DeviceFilter, line: string) => filter.AddNameFilter(line), "validNameFilters"));
		this.primIpScopeFilters = new UntypedFormControl(primIpScopeFilters, this.validateFilters((filter: DeviceFilter, line: string) => filter.AddIpScopeFilter(line), "validIpScopeFilters"));
		this.primVlanFilters = new UntypedFormControl(primVlanFilters, this.validateFilters((filter: DeviceFilter, line: string) => filter.AddVlanFilter(line), "validVlanFilters"));
		this.primOidFilters = new UntypedFormControl(primOidFilters, this.validateFilters((filter: DeviceFilter, line: string) => filter.AddOidFilter(line), "validOidFilters"));
		this.primPortFilters = new UntypedFormControl(primPortFilters, this.validateFilters((filter: DeviceFilter, line: string) => filter.AddPortFilter(line), "validPortFilters"));
		this.primDeviceCategories = primDeviceCategories;

		this.connBaseIncludeScope = new UntypedFormControl(connBaseIncludeScope, Validators.required);
		this.connNameFilters = new UntypedFormControl(connNameFilters, this.validateFilters((filter: DeviceFilter, line: string) => filter.AddNameFilter(line), "validNameFilters"));
		this.connIpScopeFilters = new UntypedFormControl(connIpScopeFilters, this.validateFilters((filter: DeviceFilter, line: string) => filter.AddIpScopeFilter(line), "validIpScopeFilters"));
		this.connVlanFilters = new UntypedFormControl(connVlanFilters, this.validateFilters((filter: DeviceFilter, line: string) => filter.AddVlanFilter(line), "validVlanFilters"));
		this.connOidFilters = new UntypedFormControl(connOidFilters, this.validateFilters((filter: DeviceFilter, line: string) => filter.AddOidFilter(line), "validOidFilters"));
		this.connPortFilters = new UntypedFormControl(connPortFilters, this.validateFilters((filter: DeviceFilter, line: string) => filter.AddPortFilter(line), "validPortFilters"));
		this.connDeviceCategories = connDeviceCategories;

		this.form = this.fb.group({
			groupName: this.groupName,
			groupType: this.groupType,

			primBaseIncludeScope: this.primBaseIncludeScope,
			primNameFilters: this.primNameFilters,
			primIpScopeFilters: this.primIpScopeFilters,
			primVlanFilters: this.primVlanFilters,
			primOidFilters: this.primOidFilters,
			primPortFilters: this.primPortFilters,

			connBaseIncludeScope: this.connBaseIncludeScope,
			connNameFilters: this.connNameFilters,
			connIpScopeFilters: this.connIpScopeFilters,
			connVlanFilters: this.connVlanFilters,
			connOidFilters: this.connOidFilters,
			connPortFilters: this.connPortFilters,
		});

		this.updateFilterSummaries();
	}

	public get groupTypeStr(): string {
		if (this.groupType) {
			return this.groupType.value;
		}

		return "";
	}

	private static stringListToInputText(values: List<string>): string {
		let result = "";

		values.ForEach((v: string) => {
			if (result.length > 0) {
				result += "\n";
			}
			result += v;
		}, this);

		return result;
	}

	private static stringListToCommaInputText(values: List<string>): string {
		let result = "";

		if (values) {
			values.ForEach((v: string) => {
				if (result.length > 0) {
					result += ",";
				}
				result += v;
			}, this);
		}

		return result;
	}


	private static inputTextToStringList(inputText: string): List<string> {
		let result = new List<string>();

		let arrayOfLines = inputText.match(/[^\r\n]+/g);
		if (arrayOfLines) {
			arrayOfLines.forEach((line: string) => {
				result.Add(line);
			}, this);
		}

		return result;
	}

	public updateFilterSummaries(): void {
		let tmpGroup = new DeviceGroup();
		this.getChanges(tmpGroup);
		this.primFilterSummary = tmpGroup.PrimaryDeviceFilter.Summary;
		this.connFilterSummary = tmpGroup.ConnectedDeviceFilter.Summary;
	}

	public onGroupTypeChange(): void {
		let type = DeviceGroupType.Other;

		if (this.groupType) {
			switch (this.groupType.value) {
				case "dynamic":
					type = DeviceGroupType.Dynamic;
					break;
				case "static":
					type = DeviceGroupType.Static;
					break;
			}
		}

		this.setActiveIndex(type);
	}

	public ngOnChanges(changes: SimpleChanges): void {

		if (changes["target"]) {
			let type = DeviceGroupType.Other;
			if (this.target) {
				type = this.target.DeviceGroup.Type;
			}
			this.setActiveIndex(type);
		}

		this.buildForm();
		this.rebuildEntries();
	}

	private setActiveIndex(type: DeviceGroupType): void {
		switch (type) {
			case DeviceGroupType.Dynamic:
				this.activeIndex = 0;
				break;
			case DeviceGroupType.Static:
				this.activeIndex = 2;
				break;
			default:
				this.activeIndex = 0;
				break;
		}
	}

	public onPrimCategoriesChange(event): void {
		this.form.markAsDirty();
		this.updateFilterSummaries();
	}

	public onConnCategoriesChange(event): void {
		this.form.markAsDirty();
		this.updateFilterSummaries();
	}

	public get tempChanges(): boolean {
		return this.form.dirty || this.mapSettings.tempChanges;
	}

	public get tempChangesValid(): boolean {
		return this.form.valid && this.mapSettings.tempChangesValid;
	}

	public discardTempChanges(): void {
		this.buildForm();
		this.mapSettings.discardTempChanges();
	}

	public get invalidTempChanges(): string[] {
		let result: string[] = [];
		if (!this.form.valid) {
			result.push("Device Group");
		}
		if (!this.mapSettings.tempChangesValid) {
			this.mapSettings.invalidTempChanges.forEach((str: string) => {
				result.push("Device Group / " + str);
			}, this);
		}
		return result;
	}

	private validateFilters(addFunc: (DeviceFilter, string) => boolean, key: string): ValidatorFn {
		let lastValue: any = undefined;
		let lastResult: any = undefined;
		return (c: UntypedFormControl) => {
			let curValue: any = c.value;

			if (lastValue !== undefined) {
				if (curValue === lastValue) {
					return lastResult;
				}
			}

			let curResult: any = null;
			if (curValue) {
				let tmpFilter = new DeviceFilter();
				let lines = DeviceGroupComponent.inputTextToStringList(curValue);
				for (let i = 0; i < lines.Count; ++i) {
					let line = lines.Get(i);
					if (!addFunc(tmpFilter, line)) {
						curResult = {};
						curResult[key] = { valid: false };
						break;
					}
				}
			}

			lastValue = curValue;
			lastResult = curResult;

			return curResult;
		};
	}

	public applyTempChanges(): void {
		if (this.target) {
			this.mapSettings.applyTempChanges();

			this.getChanges(this.target.DeviceGroup);
			this.target.Name = this.target.DeviceGroup.Name;
			this.buildForm();
		}
	}

	private getChanges(group: DeviceGroup): void {
		//group.Clear();
		group.PrimaryDeviceFilter.Clear();
		group.ConnectedDeviceFilter.Clear();

		group.Name = this.groupName.value;

		group.Type = DeviceGroupType.Dynamic;
		switch (this.groupType.value) {
			case "static":
				group.Type = DeviceGroupType.Static;
				break;
		}

		group.PrimaryDeviceFilter.BaseIncludeScope = IncludeScope.AllDevices;
		switch (this.primBaseIncludeScope.value) {
			case "AllNetworkDevices":
				group.PrimaryDeviceFilter.BaseIncludeScope = IncludeScope.AllNetworkDevices;
				break;
			case "AllSnmpDevices":
				group.PrimaryDeviceFilter.BaseIncludeScope = IncludeScope.AllSnmpDevices;
				break;
		}

		this.getFilters(this.primNameFilters, (line: string) => group.PrimaryDeviceFilter.AddNameFilter(line));
		this.getFilters(this.primIpScopeFilters, (line: string) => group.PrimaryDeviceFilter.AddIpScopeFilter(line));
		this.getFilters(this.primVlanFilters, (line: string) => group.PrimaryDeviceFilter.AddVlanFilter(line));
		this.getFilters(this.primOidFilters, (line: string) => group.PrimaryDeviceFilter.AddOidFilter(line));
		group.PrimaryDeviceFilter.ClearPortFilters();
		this.getFilters(this.primPortFilters, (line: string) => group.PrimaryDeviceFilter.AddPortFilter(line));

		this.primDeviceCategories.forEach((catName: string) => {
			group.PrimaryDeviceFilter.AddCategoryFilterByName(catName);
		}, this);

		group.ConnectedDeviceFilter.BaseIncludeScope = IncludeScope.NoDevices;
		switch (this.connBaseIncludeScope.value) {
			case "AllDevices":
				group.ConnectedDeviceFilter.BaseIncludeScope = IncludeScope.AllDevices;
				break;
			case "AllNetworkDevices":
				group.ConnectedDeviceFilter.BaseIncludeScope = IncludeScope.AllNetworkDevices;
				break;
			case "AllSnmpDevices":
				group.ConnectedDeviceFilter.BaseIncludeScope = IncludeScope.AllSnmpDevices;
				break;
		}

		this.getFilters(this.connNameFilters, (line: string) => group.ConnectedDeviceFilter.AddNameFilter(line));
		this.getFilters(this.connIpScopeFilters, (line: string) => group.ConnectedDeviceFilter.AddIpScopeFilter(line));
		this.getFilters(this.connVlanFilters, (line: string) => group.ConnectedDeviceFilter.AddVlanFilter(line));
		this.getFilters(this.connOidFilters, (line: string) => group.ConnectedDeviceFilter.AddOidFilter(line));
		group.ConnectedDeviceFilter.ClearPortFilters();
		this.getFilters(this.connPortFilters, (line: string) => group.ConnectedDeviceFilter.AddPortFilter(line));

		this.connDeviceCategories.forEach((catName: string) => {
			group.ConnectedDeviceFilter.AddCategoryFilterByName(catName);
		}, this);
	}

	private getFilters(c: UntypedFormControl, addFunc: (string) => boolean): void {
		let lines = DeviceGroupComponent.inputTextToStringList(c.value);
		lines.ForEach((line: string) => {
			addFunc(line);
		}, this);
	}

	private rebuildEntries(): void {

		// Preserve selected device if possible

		this.entries = [];
		this.selectedDeviceGroupMembers = [];
		//this.selectedEntry = null;

		if (this.target) {
			let deviceTable = this.getDeviceTable();
			deviceTable.forEach((dgm: DeviceGroupMember) => {

				let entry = new DeviceGroupMemberListEntry();

				entry.name = dgm.DisplayName;

				if (dgm.HasIpAddress) {
					entry.ipAddress = dgm.IpAddress.ToString();
					entry.ipAddressTag = dgm.IpAddress;
				}
				if (dgm.HasSystemDescription) {
					entry.description = dgm.SystemDescription;
				}
				entry.deviceGroupMemberTag = dgm;
				entry.dataKey = dgm.Key.ToString();
				//if (entry.dataKey === selectedDataKey) {
				//	this.selectedEntry = entry;
				//}

				this.entries.push(entry);

			}, this);
		}
	}

	private getDeviceTable(): DeviceGroupMember[] {
		if (this.target) {
			return this.target.DeviceGroup.Members.ToArray();
		}
		else {
			return [];
		}
	}

	public onAddDevices(): void {
		this.resetAddDevicesDevices();

		let sessionGuid = this.identityService.sessionGuid;
		let netViewGuid = this.navService.location.netViewGuid;
		let agentGuid = this.navService.location.agentGuid;
		let schedDiscGuid = this.navService.location.schedDiscGuid;
		let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;
		this.discResultsService.getFilteredDeviceList(sessionGuid, netViewGuid, agentGuid,
			schedDiscGuid, schedDiscRsltGuid, "network", null, null, false, false, false, false, false).subscribe(
				(result: DisplayDeviceListDto) => {
					this.addDevicesDevices = result;
					this.addDevicesVisible = true;
					this.changeDetector.detectChanges();
				},
				(error?) => {
					this.messageService.displayErrorMessage("Could not retrieve filtered device list");
				});
	}

	public onAddDevicesOk(selectedDevices: DisplayDeviceListEntryDto[]): void {
		if (selectedDevices && selectedDevices.length > 0) {

			let sessionGuid = this.identityService.sessionGuid;
			let netViewGuid = this.navService.location.netViewGuid;
			let agentGuid = this.navService.location.agentGuid;
			let schedDiscGuid = this.navService.location.schedDiscGuid;
			let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

			let deviceGuids = new List<string>();
			selectedDevices.forEach((device: DisplayDeviceListEntryDto) => {
				deviceGuids.Add(device.deviceGuid);
			}, this);

			this.discResultsService.getDeviceGroupMembers(sessionGuid, netViewGuid, agentGuid,
				schedDiscGuid, schedDiscRsltGuid, deviceGuids).subscribe(
					(result: GetDeviceGroupMembersResultDto) => {
						let memberKeys = new HashSet<DeviceKey>();
						this.target.DeviceGroup.Members.ForEach((dgm: DeviceGroupMember) => {
							memberKeys.Add(dgm.Key);
						}, this);

						result.DeviceGroupMembers.ForEach((dgm: DeviceGroupMember) => {
							if (!memberKeys.Contains(dgm.Key)) {
								this.target.DeviceGroup.AddMember(dgm);
							}
						}, this);

						this.rebuildEntries();
						this.form.markAsDirty();
						this.changeDetector.detectChanges();
						this.onChanges.emit(true);
					},
					(error?) => {
						this.messageService.displayErrorMessage("Could not retrieve device group members");
					});
		}

		this.resetAddDevicesDevices();
	}

	public onAddDevicesCancel(): void {
		this.resetAddDevicesDevices();
	}

	public onRemoveSingleDevice(entry: DeviceGroupMemberListEntry): void {
		this.target.DeviceGroup.RemoveMember(entry.deviceGroupMemberTag);
		this.rebuildEntries();
		this.form.markAsDirty();
	}

	public onRemoveSelectedDevices(): void {
		if (!!this.selectedDeviceGroupMembers && this.selectedDeviceGroupMembers.length > 0) {
			this.selectedDeviceGroupMembers.forEach((entry: DeviceGroupMemberListEntry) => {
				this.target.DeviceGroup.RemoveMember(entry.deviceGroupMemberTag);
			}, this);

			this.rebuildEntries();
			this.form.markAsDirty();
		}
	}

	private resetAddDevicesDevices(): void {
		this.addDevicesDevices = null;
	}

	public onPreviewPrim(): void {
		let tmpGroup = new DeviceGroup();
		this.getChanges(tmpGroup);

		this.resetPreviewPrimDevices();
		let sessionGuid = this.identityService.sessionGuid;
		let netViewGuid = this.navService.location.netViewGuid;
		let agentGuid = this.navService.location.agentGuid;
		let schedDiscGuid = this.navService.location.schedDiscGuid;
		let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;
		this.discResultsService.getFilteredDeviceList(sessionGuid, netViewGuid, agentGuid,
			schedDiscGuid, schedDiscRsltGuid, "network", tmpGroup.PrimaryDeviceFilter, null,
			false, false, false, false, false).subscribe(
				(result: DisplayDeviceListDto) => {
					this.previewPrimDevices = result;
					this.previewPrimVisible = true;
					this.changeDetector.detectChanges();
				},
				(error?) => {
					this.messageService.displayErrorMessage("Could not retrieve filtered device list");
				});
	}

	public onPreviewPrimVisibleChange(visible: boolean): void {
		if (!visible) {
			this.resetPreviewPrimDevices();
		}
	}

	private resetPreviewPrimDevices(): void {
		this.previewPrimDevices = null;
	}

	public onPreviewConn(): void {
		let tmpGroup = new DeviceGroup();
		this.getChanges(tmpGroup);

		this.resetPreviewConnDevices();
		let sessionGuid = this.identityService.sessionGuid;
		let netViewGuid = this.navService.location.netViewGuid;
		let agentGuid = this.navService.location.agentGuid;
		let schedDiscGuid = this.navService.location.schedDiscGuid;
		let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;
		this.discResultsService.getFilteredDeviceList(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
			schedDiscRsltGuid, "network", tmpGroup.PrimaryDeviceFilter, tmpGroup.ConnectedDeviceFilter,
			false, false, false, false, false).subscribe(
				(result: DisplayDeviceListDto) => {
					this.previewConnDevices = result;
					this.previewConnVisible = true;
					this.changeDetector.detectChanges();
				},
				(error?) => {
					this.messageService.displayErrorMessage("Could not retrieve filtered device list");
				});
	}

	public onPreviewConnVisibleChange(visible: boolean): void {
		if (!visible) {
			this.resetPreviewConnDevices();
		}
	}

	private resetPreviewConnDevices(): void {
		this.previewConnDevices = null;
	}

	private getConnectedDevices(primDevices: HashSet<Device>, connDevFilter: DeviceFilter): HashSet<Device> {
		let result = new HashSet<Device>();

		primDevices.ForEach((primDevice: Device) => {
			primDevice.ConnectedDevices.ForEach((connDevice: Device) => {
				if (connDevFilter.Match(connDevice)) {
					result.Add(connDevice);
				}
			}, this);
		}, this);

		return result;
	}

	public onCopyPrim(): void {
		this.resetCopyPrimFilters();

		this.deviceFilterService.getBuiltInDeviceFilters().forEach((vdf: ViewerDeviceFilterDto) => {
			this.copyPrimFilters.Add(vdf);
		}, this);

		this.network.DeviceFilters.ForEach((vdf: ViewerDeviceFilterDto) => {
			this.copyPrimFilters.Add(vdf);
		}, this);

		this.copyPrimVisible = true;
	}

	public onCopyPrimOk(selectedFilters: ViewerDeviceFilterDto[]): void {
		if (selectedFilters && selectedFilters.length > 0) {
			let selFilter = selectedFilters[0].DeviceFilter;

			let primBaseIncludeScope = "";
			let primNameFilters = "";
			let primIpScopeFilters = "";
			let primVlanFilters = "";
			let primOidFilters = "";
			let primPortFilters = "";
			let primDeviceCategories = [];

			primBaseIncludeScope = "AllDevices";
			switch (selFilter.BaseIncludeScope) {
				case IncludeScope.AllNetworkDevices:
					primBaseIncludeScope = "AllNetworkDevices";
					break;
				case IncludeScope.AllSnmpDevices:
					primBaseIncludeScope = "AllSnmpDevices";
					break;
			}

			primNameFilters = DeviceGroupComponent.stringListToInputText(selFilter.NameFilters);
			primIpScopeFilters = DeviceGroupComponent.stringListToInputText(selFilter.IpScopeFilters);
			primVlanFilters = DeviceGroupComponent.stringListToInputText(selFilter.VlanFilters);
			primOidFilters = DeviceGroupComponent.stringListToInputText(selFilter.OidFilters);
			primPortFilters = DeviceGroupComponent.stringListToInputText(selFilter.PortFilters);
			primDeviceCategories = selFilter.CategoryFilters.ToArray();

			this.primBaseIncludeScope.setValue(primBaseIncludeScope);
			this.primNameFilters.setValue(primNameFilters);
			this.primIpScopeFilters.setValue(primIpScopeFilters);
			this.primVlanFilters.setValue(primVlanFilters);
			this.primOidFilters.setValue(primOidFilters);
			this.primPortFilters.setValue(primPortFilters);
			this.primDeviceCategories = primDeviceCategories;

			this.form.markAsDirty();
			this.updateFilterSummaries();
		}

		this.resetCopyPrimFilters();
	}

	public onCopyPrimCancel(): void {
		this.resetCopyPrimFilters();
	}

	private resetCopyPrimFilters(): void {
		this.copyPrimFilters = new List<ViewerDeviceFilterDto>();
	}

	public onCopyConn(): void {
		this.resetCopyConnFilters();

		this.deviceFilterService.getBuiltInDeviceFilters().forEach((vdf: ViewerDeviceFilterDto) => {
			this.copyConnFilters.Add(vdf);
		}, this);

		this.network.DeviceFilters.ForEach((vdf: ViewerDeviceFilterDto) => {
			this.copyConnFilters.Add(vdf);
		}, this);

		this.copyConnVisible = true;
	}

	public onCopyConnOk(selectedFilters: ViewerDeviceFilterDto[]): void {
		if (selectedFilters && selectedFilters.length > 0) {
			let selFilter = selectedFilters[0].DeviceFilter;

			let connBaseIncludeScope = "";
			let connNameFilters = "";
			let connIpScopeFilters = "";
			let connVlanFilters = "";
			let connOidFilters = "";
			let connPortFilters = "";
			let connDeviceCategories = [];

			connBaseIncludeScope = "AllDevices";
			switch (selFilter.BaseIncludeScope) {
				case IncludeScope.AllNetworkDevices:
					connBaseIncludeScope = "AllNetworkDevices";
					break;
				case IncludeScope.AllSnmpDevices:
					connBaseIncludeScope = "AllSnmpDevices";
					break;
			}

			connNameFilters = DeviceGroupComponent.stringListToInputText(selFilter.NameFilters);
			connIpScopeFilters = DeviceGroupComponent.stringListToInputText(selFilter.IpScopeFilters);
			connVlanFilters = DeviceGroupComponent.stringListToInputText(selFilter.VlanFilters);
			connOidFilters = DeviceGroupComponent.stringListToInputText(selFilter.OidFilters);
			connPortFilters = DeviceGroupComponent.stringListToInputText(selFilter.PortFilters);
			connDeviceCategories = selFilter.CategoryFilters.ToArray();

			this.connBaseIncludeScope.setValue(connBaseIncludeScope);
			this.connNameFilters.setValue(connNameFilters);
			this.connIpScopeFilters.setValue(connIpScopeFilters);
			this.connVlanFilters.setValue(connVlanFilters);
			this.connOidFilters.setValue(connOidFilters);
			this.connPortFilters.setValue(connPortFilters);
			this.connDeviceCategories = connDeviceCategories;

			this.form.markAsDirty();
			this.updateFilterSummaries();
		}

		this.resetCopyConnFilters();
	}

	public onCopyConnCancel(): void {
		this.resetCopyConnFilters();
	}

	private resetCopyConnFilters(): void {
		this.copyConnFilters = new List<ViewerDeviceFilterDto>();
	}

	public onActiveIndexChange(event: any): void {
		this.activeIndex = event.index;
	}

}
